import { moduleForPath } from '~~/shared/modules/catalog'
import { canReadModule } from '~~/shared/modules/access'
import { safeRedirect, userHasPermission } from '~~/shared/utils/auth'

export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path === '/q/respond' || to.path === '/audit-room') return
  const serverOwnedRoute =
    to.path.startsWith('/api/') || /^\/login\/sso\/[^/]+(?:\/[^/]+)?\/?$/.test(to.path)
  if (serverOwnedRoute) {
    if (import.meta.client) return navigateTo(to.fullPath, { external: true })
    return
  }
  const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined
  // Capture every Nuxt-bound composable before the first await. The middleware can resume
  // without an active async Nuxt context during SSR, which made useAuth() below throw E1001.
  const auth = useAuth()
  let bootstrap: { setupRequired: boolean }
  try {
    bootstrap = await $fetch<{ setupRequired: boolean }>('/api/auth/bootstrap' as string, {
      headers,
    })
  } catch {
    return
  }
  const publicPage = to.path === '/login' || to.path === '/setup'
  if (bootstrap.setupRequired) return to.path === '/setup' ? undefined : navigateTo('/setup')
  if (!auth.loaded.value || !publicPage) await auth.refresh()
  if (to.path === '/setup') return navigateTo(auth.authenticated.value ? '/' : '/login')
  if (to.path === '/login' && auth.authenticated.value) return navigateTo('/')
  if (!publicPage && !auth.authenticated.value)
    return navigateTo({ path: '/login', query: { redirect: safeRedirect(to.fullPath) } })
  if (
    auth.authenticated.value &&
    auth.tenantSelectionRequired.value &&
    to.path !== '/tenant-select'
  )
    return navigateTo({
      path: '/tenant-select',
      query: { redirect: safeRedirect(to.fullPath) },
    })
  const requiredModule = moduleForPath(to.path)
  if (
    requiredModule &&
    (!auth.modules.value[requiredModule.key]?.canRead ||
      !userHasPermission(auth.user.value, requiredModule.accessPermission))
  ) {
    const dependencyMissing =
      auth.modules.value[requiredModule.key]?.reason === 'dependency_unavailable'
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: dependencyMissing
          ? 'Dépendance requise indisponible'
          : 'Module indisponible',
        message: dependencyMissing
          ? 'Un module requis doit être activé par votre administrateur pour ouvrir cet espace.'
          : 'Ce module est désactivé, indisponible ou non autorisé.',
      }),
    )
  }
  if (to.path.startsWith('/admin/modules') && !userHasPermission(auth.user.value, 'modules.read')) {
    return abortNavigation(
      createError({ statusCode: 403, statusMessage: 'Permission modules insuffisante' }),
    )
  }
  if (
    to.path.startsWith('/admin/platform') &&
    !auth.platformPermissions.value.includes(
      to.path === '/admin/platform/licensing' ? 'platform.licensing.read' : 'platform.tenants.read',
    )
  )
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: 'Permission plateforme insuffisante',
      }),
    )
  if (to.path.startsWith('/admin/platform')) return
  if (
    to.path.startsWith('/admin/monitoring/settings') &&
    auth.platformPermissions.value.includes('platform.monitoring.settings.manage')
  )
    return
  const notificationAdminAccess =
    userHasPermission(auth.user.value, 'notifications.admin.read') ||
    userHasPermission(auth.user.value, 'notifications.admin.manage') ||
    userHasPermission(auth.user.value, 'notifications.smtp.manage') ||
    userHasPermission(auth.user.value, 'notifications.push.devices.manage') ||
    userHasPermission(auth.user.value, 'notifications.manual.send') ||
    auth.platformPermissions.value.some((permission) =>
      permission.startsWith('platform.notifications.'),
    )
  const platformNotificationAdminPage =
    to.path.startsWith('/admin/notifications') &&
    auth.platformPermissions.value.some((permission) =>
      permission.startsWith('platform.notifications.'),
    )
  if (to.path.startsWith('/admin/notifications') && !notificationAdminAccess)
    return abortNavigation(
      createError({ statusCode: 403, statusMessage: 'Permission notification insuffisante' }),
    )
  if (
    to.path === '/admin/import-yaml' &&
    auth.mode.value !== 'platform-administration' &&
    !userHasPermission(auth.user.value, 'content.create') &&
    !userHasPermission(auth.user.value, 'content.yaml.export') &&
    !(
      canReadModule(auth.modules.value, 'quality') &&
      userHasPermission(auth.user.value, 'quality.access') &&
      userHasPermission(auth.user.value, 'quality.read') &&
      ['quality.export', 'quality.import', 'quality.import.manage'].some((permission) =>
        userHasPermission(auth.user.value, permission),
      )
    )
  )
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: 'Permission insuffisante',
        message: 'Une permission de transfert YAML ou MorningPack est nécessaire.',
      }),
    )
  if (
    to.path === '/admin' &&
    !userHasPermission(auth.user.value, 'admin.access') &&
    auth.platformPermissions.value.includes('platform.tenants.read')
  )
    return navigateTo('/admin/platform')
  if (
    to.path.startsWith('/admin') &&
    auth.mode.value !== 'platform-administration' &&
    !platformNotificationAdminPage &&
    !userHasPermission(auth.user.value, 'admin.access')
  )
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: 'Permission insuffisante',
        message:
          'Vous ne disposez pas des permissions nécessaires pour accéder à l’administration.',
      }),
    )
  const userNotificationPage = /^\/admin\/users\/[^/]+\/notifications\/?$/.test(to.path)
  if (
    userNotificationPage &&
    (!userHasPermission(auth.user.value, 'users.read') ||
      !userHasPermission(auth.user.value, 'notifications.admin.read'))
  )
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: 'Permission insuffisante',
        message:
          'La consultation des utilisateurs et des notifications est nécessaire pour accéder à cette page.',
      }),
    )
  const sectionPermission = userNotificationPage
    ? undefined
    : to.path.startsWith('/admin/inventory/settings')
      ? 'inventory.settings.manage'
      : to.path.startsWith('/admin/inventory/audit')
        ? 'inventory.audit.read'
        : to.path.startsWith('/admin/inventory/resources')
          ? 'inventory.resources.read'
          : to.path.startsWith('/admin/inventory/catalog')
            ? 'inventory.catalog.read'
            : to.path.startsWith('/admin/inventory')
              ? 'inventory.read'
              : to.path.startsWith('/admin/users')
                ? 'users.read'
                : to.path.startsWith('/admin/authentication')
                  ? 'auth.oidc.read'
                  : to.path.startsWith('/admin/groups')
                    ? 'groups.read'
                    : to.path.startsWith('/admin/roles')
                      ? 'roles.read'
                      : to.path.startsWith('/admin/incidents')
                        ? 'incidents.read'
                        : to.path.startsWith('/admin/agents/identities')
                          ? 'agents.read'
                          : to.path.startsWith('/admin/agents')
                            ? 'agents.read'
                            : to.path.startsWith('/admin/monitoring/settings')
                              ? 'monitoring.read'
                              : to.path.startsWith('/admin/monitoring/status-pages')
                                ? 'monitoring.status-pages.read'
                                : to.path.startsWith('/admin/monitoring')
                                  ? 'monitoring.read'
                                  : to.path.startsWith('/admin/notifications')
                                    ? undefined
                                    : to.path.startsWith('/admin/announcements')
                                      ? 'announcements.read'
                                      : to.path.startsWith('/admin/content') ||
                                          to.path.startsWith('/admin/applications')
                                        ? 'content.read'
                                        : to.path.startsWith('/admin/dashboards/personalization')
                                          ? 'dashboards.personalization.manage'
                                          : to.path.startsWith('/admin/dashboards')
                                            ? 'dashboards.read'
                                            : undefined
  if (
    sectionPermission &&
    auth.mode.value !== 'platform-administration' &&
    !userHasPermission(auth.user.value, sectionPermission)
  )
    return abortNavigation(
      createError({
        statusCode: 403,
        statusMessage: 'Permission insuffisante',
        message: 'Vous ne disposez pas des permissions nécessaires pour cette rubrique.',
      }),
    )
})
